import React, { useState } from "react";
import axios from "axios";
import {
  AlertCircle, Loader2, Upload, CheckCircle2, User as UserIcon, MapPin,
  Calendar, Globe, Phone, Image as ImageIcon, Lock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Modale obbligatoria al primo accesso del socio.
 * Raccoglie dati anagrafici richiesti per legge (registro nazionale).
 * Non chiudibile: il socio DEVE completare tutti i campi per usare la dashboard.
 */
export default function OnboardingModal({ user, onComplete }) {
  const [step, setStep] = useState(1); // 1=anagrafica, 2=foto, 3=accettazione
  const [form, setForm] = useState({
    sesso: "",
    nome: user?.nome || "",
    cognome: user?.cognome || "",
    data_nascita: "",
    nazione_nascita: "Italia",
    comune_nascita: "",
    cap_nascita: "",
    prov_nascita: "",
    nazione_residenza: "Italia",
    comune_residenza: user?.citta || "",
    cap_residenza: "",
    prov_residenza: user?.provincia || "",
    indirizzo_residenza: "",
    telefono: user?.telefono || "",
  });
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoId, setFotoId] = useState(null);
  const [fotoUploading, setFotoUploading] = useState(false);
  const [accettazione, setAccettazione] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isStep1Valid = () =>
    form.sesso &&
    form.nome.trim() && form.cognome.trim() && form.data_nascita &&
    form.comune_nascita.trim() && form.cap_nascita && form.prov_nascita &&
    form.comune_residenza.trim() && form.cap_residenza && form.prov_residenza &&
    form.indirizzo_residenza.trim() && form.telefono.trim();

  const handleFotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { setError("La foto deve essere max 500 KB."); return; }
    setError("");
    setFotoFile(file);
    setFotoUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/me/upload-foto-tessera`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      setFotoId(data.id);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore caricamento foto");
      setFotoFile(null);
    } finally {
      setFotoUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!isStep1Valid()) { setStep(1); setError("Compila tutti i campi anagrafici"); return; }
    if (!accettazione) { setError("Devi accettare i termini per procedere"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/api/me/onboarding`, {
        ...form,
        prov_nascita: form.prov_nascita.toUpperCase().slice(0, 4),
        prov_residenza: form.prov_residenza.toUpperCase().slice(0, 4),
        foto_tessera_id: fotoId,
      }, { withCredentials: true });
      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || "Errore durante l'invio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="onboarding-modal"
      className="fixed inset-0 z-[100] bg-[#0a1020]/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
    >
      <div className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl overflow-hidden my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0a1f55] to-[#102a6b] text-white p-6 md:p-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b]/20 text-[#fbbf24] text-[10.5px] font-extrabold uppercase tracking-[0.2em] mb-3">
            <Lock className="h-3.5 w-3.5" /> Completa la tua iscrizione
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold">Benvenuto in A.C.D.T., {form.nome || "Socio"}!</h2>
          <p className="mt-2 text-[14px] text-slate-200">
            Per attivare la tua area soci, completa i dati anagrafici richiesti per legge.
            Tessera: <strong className="text-[#fbbf24]">{user?.numeroTessera}</strong> · Pacchetto: <strong className="text-[#fbbf24] capitalize">{user?.pacchetto || "—"}</strong>
          </p>

          {/* Stepper */}
          <div className="mt-5 flex items-center gap-2">
            {[
              { n: 1, label: "Dati anagrafici" },
              { n: 2, label: "Foto tessera" },
              { n: 3, label: "Accettazione" },
            ].map((s, i) => (
              <React.Fragment key={s.n}>
                {i > 0 && <div className={`h-px flex-1 ${step >= s.n ? "bg-[#f59e0b]" : "bg-white/15"}`} />}
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-bold ${
                  step >= s.n ? "bg-[#f59e0b] text-[#0a1020]" : "bg-white/10 text-slate-300"
                }`}>
                  {step > s.n ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="font-extrabold">{s.n}</span>}
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8">
          {step === 1 && (
            <div data-testid="onboarding-step-1" className="space-y-5">
              <SectionHeader icon={UserIcon} title="1. Dati anagrafici" />

              <div>
                <Label className="text-[13px] font-semibold text-[#0a1f55]">Sesso *</Label>
                <div className="mt-2 flex gap-3">
                  {[{ v: "M", l: "Maschio" }, { v: "F", l: "Femmina" }].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      data-testid={`sesso-${o.v}`}
                      onClick={() => setForm({ ...form, sesso: o.v })}
                      className={`flex-1 h-11 rounded-lg border-2 font-bold text-[13px] transition-colors ${
                        form.sesso === o.v ? "border-[#102a6b] bg-[#102a6b] text-white" : "border-slate-200 hover:border-[#102a6b]/40"
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <F label="Nome *" testid="onb-nome" value={form.nome} onChange={onChange("nome")} />
                <F label="Cognome *" testid="onb-cognome" value={form.cognome} onChange={onChange("cognome")} />
              </div>

              <F icon={Calendar} label="Data di nascita *" testid="onb-data-nascita" type="date" value={form.data_nascita} onChange={onChange("data_nascita")} />

              <FieldGroup icon={Globe} title="Luogo di nascita">
                <F label="Nazione *" testid="onb-naz-nascita" value={form.nazione_nascita} onChange={onChange("nazione_nascita")} />
                <div className="grid grid-cols-[1fr_100px_80px] gap-2">
                  <F label="Comune *" testid="onb-com-nascita" value={form.comune_nascita} onChange={onChange("comune_nascita")} />
                  <F label="CAP *" testid="onb-cap-nascita" value={form.cap_nascita} onChange={onChange("cap_nascita")} maxLength={10} />
                  <F label="Prov *" testid="onb-prov-nascita" value={form.prov_nascita} onChange={onChange("prov_nascita")} maxLength={4} />
                </div>
              </FieldGroup>

              <FieldGroup icon={MapPin} title="Residenza">
                <F label="Nazione *" testid="onb-naz-res" value={form.nazione_residenza} onChange={onChange("nazione_residenza")} />
                <div className="grid grid-cols-[1fr_100px_80px] gap-2">
                  <F label="Comune *" testid="onb-com-res" value={form.comune_residenza} onChange={onChange("comune_residenza")} />
                  <F label="CAP *" testid="onb-cap-res" value={form.cap_residenza} onChange={onChange("cap_residenza")} maxLength={10} />
                  <F label="Prov *" testid="onb-prov-res" value={form.prov_residenza} onChange={onChange("prov_residenza")} maxLength={4} />
                </div>
                <F label="Indirizzo *" testid="onb-indirizzo" value={form.indirizzo_residenza} onChange={onChange("indirizzo_residenza")} placeholder="Via/Piazza e numero civico" />
              </FieldGroup>

              <F icon={Phone} label="Telefono *" testid="onb-telefono" value={form.telefono} onChange={onChange("telefono")} placeholder="+39 333 1234567" />
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-2" className="space-y-5">
              <SectionHeader icon={ImageIcon} title="2. Foto tessera (facoltativa)" />
              <p className="text-[13px] text-slate-600">
                Carica una tua foto che comparirà sulla tessera digitale.
                Formati: JPG, PNG, PDF · Dimensioni consigliate 300x240px · Max 500 KB.
              </p>

              <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                <input
                  id="foto-input"
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={handleFotoUpload}
                  data-testid="onb-foto-input"
                  className="hidden"
                />
                {!fotoFile && (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                    <label htmlFor="foto-input" className="inline-block cursor-pointer px-5 py-2.5 rounded-lg bg-[#102a6b] hover:bg-[#0a1f55] text-white text-[13px] font-bold">
                      Scegli foto
                    </label>
                    <p className="mt-3 text-[11.5px] text-slate-500">Puoi saltare questo step e caricarla in seguito</p>
                  </>
                )}
                {fotoFile && (
                  <div data-testid="foto-preview">
                    <div className="text-[13px] font-bold text-[#0a1f55]">{fotoFile.name}</div>
                    <div className="text-[11.5px] text-slate-500">{(fotoFile.size / 1024).toFixed(1)} KB</div>
                    {fotoUploading ? (
                      <div className="mt-3 inline-flex items-center gap-2 text-[12px] text-slate-500">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Upload in corso…
                      </div>
                    ) : fotoId ? (
                      <div className="mt-3 inline-flex items-center gap-2 text-[12px] font-bold text-emerald-700">
                        <CheckCircle2 className="h-4 w-4" /> Foto caricata
                      </div>
                    ) : null}
                    <div className="mt-3">
                      <label htmlFor="foto-input" className="text-[12px] text-[#102a6b] font-semibold cursor-pointer hover:underline">
                        Cambia foto
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-3" className="space-y-5">
              <SectionHeader icon={CheckCircle2} title="3. Accettazione e conferma" />

              <div className="rounded-xl bg-slate-50 border border-slate-200 p-5 max-h-60 overflow-y-auto text-[12.5px] text-slate-700 leading-relaxed">
                <p>
                  Confermo che i dati sopra inseriti sono veritieri e corretti. Autorizzo A.C.D.T.
                  — Associazione Consulenti Digitali Tecnologici — al trattamento dei dati personali
                  ai sensi del Reg. UE 2016/679 (GDPR) per le seguenti finalità: gestione del rapporto
                  associativo, iscrizione al Registro Nazionale dei Professionisti di Categoria
                  (L. 4/2013), invio di comunicazioni istituzionali, formazione e aggiornamento
                  professionale obbligatorio (crediti CFDT).
                </p>
                <p className="mt-2">
                  I dati saranno conservati per la durata del rapporto associativo e per gli
                  obblighi di legge successivi. È sempre possibile esercitare i diritti GDPR
                  contattando privacy@assoacdt.com.
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="onb-accettazione"
                  checked={accettazione}
                  onChange={(e) => setAccettazione(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#102a6b]"
                />
                <span className="text-[13px] text-slate-700">
                  Dichiaro di aver letto e accetto i termini, e autorizzo A.C.D.T. al trattamento dei miei dati personali per le finalità sopra indicate.
                </span>
              </label>

              <div className="rounded-xl bg-[#fff7ed] border border-[#fbbf24]/30 p-4 text-[12.5px] text-[#92400e]">
                <strong>Riepilogo:</strong> {form.nome} {form.cognome}, nato/a il {form.data_nascita} a {form.comune_nascita} ({form.prov_nascita}). Residente in {form.indirizzo_residenza}, {form.comune_residenza} ({form.prov_residenza}). Tel: {form.telefono}.
              </div>
            </div>
          )}

          {error && (
            <div data-testid="onboarding-error" className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px] font-semibold p-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}

          {/* Footer / nav */}
          <div className="mt-6 flex items-center justify-between gap-3 pt-5 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              data-testid="onb-back"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="border-slate-300"
            >
              Indietro
            </Button>
            {step < 3 ? (
              <Button
                type="button"
                data-testid="onb-next"
                onClick={() => {
                  if (step === 1 && !isStep1Valid()) { setError("Compila tutti i campi obbligatori"); return; }
                  setError("");
                  setStep(step + 1);
                }}
                className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-extrabold px-8"
              >
                Avanti →
              </Button>
            ) : (
              <Button
                type="button"
                data-testid="onb-submit"
                onClick={handleSubmit}
                disabled={saving || !accettazione}
                className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold px-8"
              >
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : "Conferma e attiva area soci"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
      <Icon className="h-5 w-5 text-[#f59e0b]" />
      <h3 className="text-lg font-extrabold text-[#0a1f55]">{title}</h3>
    </div>
  );
}

function FieldGroup({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-3">
      <div className="flex items-center gap-2 text-[12px] uppercase tracking-wider font-extrabold text-[#f59e0b]">
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      {children}
    </div>
  );
}

function F({ icon: Icon, label, testid, value, onChange, type = "text", placeholder, maxLength }) {
  return (
    <div>
      <Label className="text-[12.5px] font-semibold text-[#0a1f55]">{label}</Label>
      <div className="relative mt-1">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />}
        <Input
          data-testid={testid}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          className={`h-11 ${Icon ? "pl-10" : ""}`}
        />
      </div>
    </div>
  );
}
